/**
 * LiveShoppingSession - Interface vendeur pour session Live Shopping
 * Layout: Vidéo compacte + Catalogue POS + Panier/Chat
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Video,
  VideoOff,
  Mic,
  MicOff,
  Phone,
  ShoppingCart,
  Trash2,
  Send,
  CheckCircle,
  Loader2,
  ChevronLeft,
  Search,
  Scale,
  X,
  Bell,
  Check,
  XCircle,
  Clock,
  GripVertical,
} from 'lucide-react';
import { useWebRTC, useLiveSession } from '../../hooks/live-shopping';
import { useTenant } from '../../contexts/TenantContext';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import { useScale } from '../../hooks/useScale';
import { useLiveShoppingStore } from '../../hooks/useLiveShoppingStore';
import ScaleModal from '../../components/pos/ScaleModal';
import { logger } from '../../utils/logger';

// Types
interface Category {
  id: string;
  name: string;
  slug: string;
  icon?: string | null;
  parent_id?: string | null;
  active: boolean;
}

interface Product {
  id: string;
  name: string;
  code?: string;
  barcode?: string;
  image_url?: string;
  is_weighted?: boolean;
  bio?: boolean;
  vegan?: boolean;
  category_id?: string;
  prices?: Array<{
    id: string;
    amount_cents: number;
    currency: string;
    tax_class?: string;
  }>;
}

export default function LiveShoppingSession() {
  const { sessionId, vendorId: routeVendorId } = useParams<{ sessionId: string; vendorId?: string }>();
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const { user } = useAuth();
  const vendorId = routeVendorId || sessionStorage.getItem('live_shopping_vendor_id') || '';
  const tenantId = tenant?.id || '';

  // Store pour nettoyer l'état lors de la fermeture
  const { closeSession } = useLiveShoppingStore();

  // Vérification de sécurité : un VENDOR ne peut accéder qu'à sa propre session
  // ADMIN et MANAGER peuvent accéder à toutes les sessions
  const isAuthorized = !user || user.role === 'ADMIN' || user.role === 'MANAGER' || user.id === vendorId;

  if (!isAuthorized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="text-6xl mb-4">🚫</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Accès non autorisé</h1>
          <p className="text-gray-600 mb-6">
            Vous ne pouvez accéder qu'à vos propres sessions Live Shopping.
          </p>
          <button
            onClick={() => navigate('/live-shopping')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retour au dashboard
          </button>
        </div>
      </div>
    );
  }

  // Video ref
  const localVideoRef = useRef<HTMLVideoElement>(null);

  // Live session - utilise sendCartAction pour envoyer les actions au client
  // Passer initialSessionId pour permettre la gestion correcte de rejoin_session_required
  // AVANT que joinSession ne soit appelé
  const {
    connect: connectSession,
    joinSession,
    messages,
    sendMessage,
    suggestions,
    respondToSuggestion,
    isConnected: sessionConnected,
    sessionId: currentSessionId,
    // E-commerce cart integration
    sendCartAction,
    vendorCartState, // Panier du client (synchronisé via WebSocket)
    // Session control with fees
    cancelSessionWithFees,
    finalizeSessionWithoutCustomer,
    endVendorSession,
  } = useLiveSession({
    role: 'vendor',
    vendorId,
    initialSessionId: sessionId, // From URL params - permet de gérer rejoin_session_required
  });

  // État déconnexion client
  const [customerDisconnected, setCustomerDisconnected] = useState(false);
  const [reconnectionDeadline, setReconnectionDeadline] = useState<Date | null>(null);
  const [showCancelMenu, setShowCancelMenu] = useState(false);

  // WebRTC
  const {
    isConnected: rtcConnected,
    isConnecting,
    connectionState,
    localStream,
    startCamera,
    connect: connectRTC,
    disconnect: disconnectRTC,
  } = useWebRTC({
    sessionId: sessionId || '',
    tenantId,
    role: 'vendor',
    vendorId,
  });

  // Local state
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const [isEnding, setIsEnding] = useState(false);

  // Video window state (draggable & resizable)
  const [videoPosition, setVideoPosition] = useState({ x: 12, y: 12 }); // Position relative to container
  const [videoSize, setVideoSize] = useState({ width: 280, height: 210 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const videoContainerRef = useRef<HTMLDivElement>(null);

  // Catalog state
  const [categories, setCategories] = useState<Category[]>([]);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [displayedProducts, setDisplayedProducts] = useState<Product[]>([]);
  const [currentCategoryId, setCurrentCategoryId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [breadcrumb, setBreadcrumb] = useState<Array<{ id: string | null; name: string }>>([
    { id: null, name: 'Catalogue' }
  ]);
  const [catalogLoading, setCatalogLoading] = useState(true);

  // Weighted product handling (simplified for Live Shopping)
  const [showWeightDialog, setShowWeightDialog] = useState(false);
  const [selectedWeightedProduct, setSelectedWeightedProduct] = useState<Product | null>(null);
  const [manualWeight, setManualWeight] = useState('');

  // Scale
  const { currentWeight, connected: scaleConnected, connect: connectScale } = useScale();
  const scaleWeight = currentWeight?.weight ?? 0;
  const scaleStable = currentWeight?.stable ?? false;
  const [showScaleModal, setShowScaleModal] = useState(false);

  // Refs for initialization
  const connectSessionRef = useRef(connectSession);
  const startCameraRef = useRef(startCamera);
  const connectRTCRef = useRef(connectRTC);
  const disconnectRTCRef = useRef(disconnectRTC);
  const hasInitializedRef = useRef(false);

  // Keep refs updated
  useEffect(() => {
    connectSessionRef.current = connectSession;
    startCameraRef.current = startCamera;
    connectRTCRef.current = connectRTC;
    disconnectRTCRef.current = disconnectRTC;
  });

  // Keep joinSession ref updated
  const joinSessionRef = useRef(joinSession);
  useEffect(() => {
    joinSessionRef.current = joinSession;
  }, [joinSession]);

  // Initialize connections
  useEffect(() => {
    // Skip if tenant not yet loaded (critical for popup windows)
    if (!tenantId) {
      logger.log('[LiveShoppingSession] Waiting for tenant to load...');
      return;
    }

    // Skip if already initialized for this session
    if (hasInitializedRef.current) {
      return;
    }
    hasInitializedRef.current = true;

    logger.log('[LiveShoppingSession] Initializing session:', sessionId, 'tenant:', tenantId);

    const init = async () => {
      try {
        await connectSessionRef.current();
        logger.log('[LiveShoppingSession] WebSocket connected');

        // Join the session to enable cart sync
        if (sessionId) {
          await joinSessionRef.current(sessionId);
          logger.log('[LiveShoppingSession] Joined session room');
        }

        await startCameraRef.current('720p');
        logger.log('[LiveShoppingSession] Camera started');
      } catch (err) {
        logger.error('[LiveShoppingSession] Init error:', err);
      }
    };
    init();

    // Don't call disconnectRTC in cleanup - useWebRTC handles its own cleanup
    // Calling disconnect() here would stop the camera on StrictMode remount
  }, [sessionId, tenantId]);

  // Connect WebRTC after camera starts
  useEffect(() => {
    if (localStream && sessionId && !rtcConnected && !isConnecting) {
      connectRTCRef.current();
    }
  }, [localStream, sessionId, rtcConnected, isConnecting]);

  // Update local video element
  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  // Load catalog data
  useEffect(() => {
    loadCatalog();
  }, []);

  // Gérer la déconnexion du client (détectée via WebRTC)
  useEffect(() => {
    if (!rtcConnected && sessionConnected && vendorCartState.length > 0) {
      // Le client s'est déconnecté mais la session est toujours active
      // Le timer de 60s est géré côté serveur
      setCustomerDisconnected(true);
      setReconnectionDeadline(new Date(Date.now() + 60000));
    }
  }, [rtcConnected, sessionConnected, vendorCartState.length]);

  // Reset si le client se reconnecte
  useEffect(() => {
    if (rtcConnected && customerDisconnected) {
      setCustomerDisconnected(false);
      setReconnectionDeadline(null);
    }
  }, [rtcConnected, customerDisconnected]);

  // Filter products when category or search changes
  useEffect(() => {
    filterProducts();
  }, [currentCategoryId, searchQuery, allProducts, categories]);

  const loadCatalog = async () => {
    try {
      setCatalogLoading(true);
      const [categoriesRes, productsRes] = await Promise.all([
        api.getCategories(),
        api.getProducts({ active: true }),
      ]);

      const cats = Array.isArray(categoriesRes) ? categoriesRes : (categoriesRes.data || []);
      const prods = Array.isArray(productsRes) ? productsRes : (productsRes.data || []);

      setCategories(cats.filter((c: Category) => c.active));
      setAllProducts(prods.filter((p: Product) => p.prices && p.prices.length > 0));
    } catch (error) {
      logger.error('Error loading catalog:', error);
    } finally {
      setCatalogLoading(false);
    }
  };

  const getAllSubCategoryIds = (categoryId: string): string[] => {
    const subCategories = categories.filter((cat) => cat.parent_id === categoryId);
    let allIds = [categoryId];
    for (const sub of subCategories) {
      allIds = allIds.concat(getAllSubCategoryIds(sub.id));
    }
    return allIds;
  };

  const filterProducts = () => {
    let filtered = allProducts;

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = allProducts.filter(
        (p) =>
          p.name.toLowerCase().includes(query) ||
          p.code?.toLowerCase().includes(query) ||
          p.barcode?.includes(query)
      );
    } else if (currentCategoryId) {
      // Category filter
      const categoryIds = getAllSubCategoryIds(currentCategoryId);
      filtered = allProducts.filter((p) => p.category_id && categoryIds.includes(p.category_id));
    }

    setDisplayedProducts(filtered);
  };

  const navigateToCategory = (categoryId: string | null, _categoryName?: string) => {
    setCurrentCategoryId(categoryId);
    setSearchQuery('');

    if (categoryId === null) {
      setBreadcrumb([{ id: null, name: 'Catalogue' }]);
    } else {
      // Find category hierarchy
      const path: Array<{ id: string | null; name: string }> = [{ id: null, name: 'Catalogue' }];
      let current = categories.find((c) => c.id === categoryId);
      const hierarchy: Category[] = [];

      while (current) {
        hierarchy.unshift(current);
        current = current.parent_id ? categories.find((c) => c.id === current!.parent_id) : undefined;
      }

      hierarchy.forEach((cat) => {
        path.push({ id: cat.id, name: cat.name });
      });

      setBreadcrumb(path);
    }
  };

  const getSubCategories = () => {
    return categories.filter((c) => c.parent_id === currentCategoryId);
  };

  // Add product to client's e-commerce cart via WebSocket
  const handleAddProduct = (product: Product) => {
    if (product.is_weighted) {
      // Open weight dialog for weighted products
      setSelectedWeightedProduct(product);
      setManualWeight(scaleWeight > 0 ? String(scaleWeight) : '');
      setShowWeightDialog(true);
    } else {
      // Envoyer l'action au client - le produit sera ajouté avec addedBy: 'vendor' et prepared: true
      sendCartAction({
        type: 'add',
        product: product, // Envoyer le produit complet
        quantity: 1,
      });
    }
  };

  // Handle weight confirm for weighted products
  const handleWeightConfirm = () => {
    if (selectedWeightedProduct && manualWeight) {
      const weightGrams = parseFloat(manualWeight);
      if (isNaN(weightGrams) || weightGrams <= 0) return;

      // Envoyer l'action au client avec la quantité en grammes
      sendCartAction({
        type: 'add',
        product: selectedWeightedProduct,
        quantity: weightGrams, // En grammes
      });

      setShowWeightDialog(false);
      setSelectedWeightedProduct(null);
      setManualWeight('');
    }
  };

  // Cancel weight dialog
  const handleWeightCancel = () => {
    setShowWeightDialog(false);
    setSelectedWeightedProduct(null);
    setManualWeight('');
  };

  // Video drag & resize handlers
  const handleDragStart = (e: React.MouseEvent) => {
    if (isResizing) return;
    setIsDragging(true);
    setDragStart({
      x: e.clientX - videoPosition.x,
      y: e.clientY - videoPosition.y,
    });
  };

  const handleResizeStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsResizing(true);
    setDragStart({
      x: e.clientX,
      y: e.clientY,
    });
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const newX = Math.max(0, e.clientX - dragStart.x);
        const newY = Math.max(0, e.clientY - dragStart.y);
        setVideoPosition({ x: newX, y: newY });
      } else if (isResizing) {
        const deltaX = e.clientX - dragStart.x;
        const deltaY = e.clientY - dragStart.y;
        setVideoSize((prev) => ({
          width: Math.max(160, Math.min(1200, prev.width + deltaX)),
          height: Math.max(120, Math.min(900, prev.height + deltaY)),
        }));
        setDragStart({ x: e.clientX, y: e.clientY });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, dragStart]);

  // Toggle audio/video
  const handleToggleMute = () => {
    if (localStream) {
      localStream.getAudioTracks().forEach((track) => {
        track.enabled = !track.enabled;
      });
      setIsMuted(!isMuted);
    }
  };

  const handleToggleCamera = () => {
    if (localStream) {
      localStream.getVideoTracks().forEach((track) => {
        track.enabled = !track.enabled;
      });
      setIsCameraOff(!isCameraOff);
    }
  };

  // Chat
  const handleSendMessage = () => {
    if (chatMessage.trim()) {
      sendMessage(chatMessage);
      setChatMessage('');
    }
  };

  // Fermer le popup ou naviguer vers le workspace si pas dans un popup
  const closePopupOrNavigate = useCallback(() => {
    // Vérifier si on est dans un popup (window.opener existe)
    if (window.opener) {
      // On est dans un popup - fermer la fenêtre
      window.close();
    } else {
      // Pas dans un popup - naviguer vers le workspace
      navigate('/pos?tab=live');
    }
  }, [navigate]);

  // Cart total - calculé depuis le panier du client synchronisé
  const cartTotal = vendorCartState.reduce((sum, item) => sum + item.total_price_cents, 0);

  // Vendeur termine sa session - imprime ticket articles préparés
  const handleEndVendorSession = async () => {
    setIsEnding(true);
    try {
      // Récupérer le nom du vendeur depuis la session storage ou tenant
      const vendorName = sessionStorage.getItem('vendor_name') || tenant?.name || 'Vendeur';

      const result = await endVendorSession(vendorName);
      if (result?.success) {
        const message = result.preparedItemsCount > 0
          ? `${result.preparedItemsCount} article(s) préparé(s) - Ticket imprimé !`
          : 'Session terminée (aucun article préparé)';
        alert(message);

        // Déconnecter WebRTC
        disconnectRTCRef.current();

        // IMPORTANT: Nettoyer le store avant de fermer
        closeSession();

        // Fermer le popup ou retourner au workspace
        closePopupOrNavigate();
      } else if (result?.error) {
        alert(`Erreur: ${result.error}`);
      }
    } catch {
      // End vendor session error
    } finally {
      setIsEnding(false);
    }
  };

  // Finaliser même si le client est déconnecté
  const handleFinalizeWithoutCustomer = async () => {
    setIsEnding(true);
    try {
      const result = await finalizeSessionWithoutCustomer();
      if (result?.success) {
        alert(`Commande ${result.orderNumber} créée avec succès !`);
        disconnectRTCRef.current();

        // Nettoyer le store avant de fermer
        closeSession();

        // Fermer le popup ou retourner au workspace
        closePopupOrNavigate();
      }
    } catch {
      // Finalize error
    } finally {
      setIsEnding(false);
    }
  };

  // Annuler (vendeur - pas de frais pour le client)
  const handleCancel = async () => {
    setShowCancelMenu(false);
    await cancelSessionWithFees('vendor', 'manual');
    disconnectRTCRef.current();

    // Nettoyer le store avant de fermer
    closeSession();

    // Fermer le popup ou retourner au workspace
    closePopupOrNavigate();
  };

  // Annuler pour raison technique
  const handleCancelTechnical = async () => {
    setShowCancelMenu(false);
    await cancelSessionWithFees('vendor', 'technical');
    disconnectRTCRef.current();

    // Nettoyer le store avant de fermer
    closeSession();

    // Fermer le popup ou retourner au workspace
    closePopupOrNavigate();
  };

  const subCategories = getSubCategories();

  return (
    <div className="h-screen bg-gray-100 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b px-4 py-2 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${rtcConnected ? 'bg-green-500' : customerDisconnected ? 'bg-red-500' : 'bg-yellow-500'} animate-pulse`} />
          <span className="font-medium text-gray-900">Session Live Shopping</span>
          <span className="text-gray-500 text-sm">{connectionState || 'Connexion...'}</span>

          {/* Alerte client déconnecté */}
          {customerDisconnected && (
            <div className="flex items-center gap-2 bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm">
              <span className="animate-pulse">Client déconnecté</span>
              {reconnectionDeadline && (
                <span className="font-mono">
                  ({Math.max(0, Math.ceil((reconnectionDeadline.getTime() - Date.now()) / 1000))}s)
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Menu Annuler avec dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowCancelMenu(!showCancelMenu)}
              className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm flex items-center gap-1"
            >
              Annuler
              <ChevronLeft className="w-4 h-4 rotate-[-90deg]" />
            </button>
            {showCancelMenu && (
              <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg z-50 min-w-[200px]">
                <button
                  onClick={handleCancel}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex flex-col"
                >
                  <span className="font-medium">Client indisponible</span>
                  <span className="text-xs text-gray-500">Pas de frais pour le client</span>
                </button>
                <button
                  onClick={handleCancelTechnical}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex flex-col border-t"
                >
                  <span className="font-medium">Problème technique</span>
                  <span className="text-xs text-gray-500">Pas de frais pour le client</span>
                </button>
              </div>
            )}
          </div>

          {/* Bouton Finaliser (si client déconnecté) */}
          {customerDisconnected && vendorCartState.length > 0 && (
            <button
              onClick={handleFinalizeWithoutCustomer}
              disabled={isEnding}
              className="px-4 py-1.5 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50"
            >
              {isEnding ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              Finaliser sans client ({(cartTotal / 100).toFixed(2)} €)
            </button>
          )}

          {/* Bouton Terminer (vendeur quitte, le client continue seul) */}
          {!customerDisconnected && vendorCartState.length > 0 && (
            <button
              onClick={handleEndVendorSession}
              disabled={isEnding}
              className="px-4 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50"
            >
              {isEnding ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              Terminer ({(cartTotal / 100).toFixed(2)} €)
            </button>
          )}

          {/* Info panier vide */}
          {!customerDisconnected && vendorCartState.length === 0 && (
            <div className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-sm">
              Panier vide - en attente d'articles
            </div>
          )}
        </div>
      </div>

      {/* Overlay click handler pour fermer le menu */}
      {showCancelMenu && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowCancelMenu(false)}
        />
      )}

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden relative" ref={videoContainerRef}>
        {/* Floating Video Window - Draggable & Resizable */}
        <div
          className={`absolute z-30 rounded-xl overflow-hidden shadow-2xl border-2 border-gray-700 ${
            isDragging ? 'cursor-grabbing' : ''
          }`}
          style={{
            left: videoPosition.x,
            top: videoPosition.y,
            width: videoSize.width,
            height: videoSize.height,
          }}
        >
          {/* Drag handle bar */}
          <div
            className="bg-gray-800 px-2 py-1 flex items-center justify-between cursor-grab active:cursor-grabbing select-none"
            onMouseDown={handleDragStart}
          >
            <div className="flex items-center gap-2">
              <GripVertical className="w-4 h-4 text-gray-400" />
              <span className="text-xs text-gray-400">Aperçu caméra</span>
            </div>
            <div className="flex items-center gap-1">
              {/* Size presets */}
              <button
                onClick={() => setVideoSize({ width: 200, height: 150 })}
                className="px-1.5 py-0.5 text-[10px] text-gray-400 hover:text-white hover:bg-gray-700 rounded"
                title="Petite (200x150)"
              >
                S
              </button>
              <button
                onClick={() => setVideoSize({ width: 320, height: 240 })}
                className="px-1.5 py-0.5 text-[10px] text-gray-400 hover:text-white hover:bg-gray-700 rounded"
                title="Moyenne (320x240)"
              >
                M
              </button>
              <button
                onClick={() => setVideoSize({ width: 480, height: 360 })}
                className="px-1.5 py-0.5 text-[10px] text-gray-400 hover:text-white hover:bg-gray-700 rounded"
                title="Grande (480x360)"
              >
                L
              </button>
              <button
                onClick={() => setVideoSize({ width: 640, height: 480 })}
                className="px-1.5 py-0.5 text-[10px] text-gray-400 hover:text-white hover:bg-gray-700 rounded"
                title="Très grande (640x480)"
              >
                XL
              </button>
              <button
                onClick={() => setVideoSize({ width: 960, height: 720 })}
                className="px-1.5 py-0.5 text-[10px] text-gray-400 hover:text-white hover:bg-gray-700 rounded"
                title="HD (960x720)"
              >
                HD
              </button>
            </div>
          </div>

          {/* Video content */}
          <div className="relative bg-black" style={{ height: `calc(100% - 28px)` }}>
            <video
              ref={localVideoRef}
              autoPlay
              muted
              playsInline
              className="w-full h-full object-cover"
            />
            {isCameraOff && (
              <div className="absolute inset-0 bg-gray-800 flex items-center justify-center">
                <VideoOff className="w-10 h-10 text-gray-400" />
              </div>
            )}

            {/* Live indicator */}
            <div className="absolute top-2 left-2 flex items-center gap-1.5 bg-red-600 text-white px-2 py-0.5 rounded-full text-xs font-medium">
              <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
              LIVE
            </div>

            {/* Session ID */}
            {currentSessionId && (
              <div className="absolute top-2 right-2 bg-black/60 text-white px-1.5 py-0.5 rounded text-xs">
                #{currentSessionId.slice(0, 6)}
              </div>
            )}

            {/* Video controls */}
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-black/70 rounded-full px-3 py-1.5">
              <button
                onClick={handleToggleMute}
                className={`p-1.5 rounded-full transition-colors ${isMuted ? 'bg-red-600' : 'bg-gray-700 hover:bg-gray-600'}`}
              >
                {isMuted ? <MicOff className="w-4 h-4 text-white" /> : <Mic className="w-4 h-4 text-white" />}
              </button>
              <button
                onClick={handleToggleCamera}
                className={`p-1.5 rounded-full transition-colors ${isCameraOff ? 'bg-red-600' : 'bg-gray-700 hover:bg-gray-600'}`}
              >
                {isCameraOff ? <VideoOff className="w-4 h-4 text-white" /> : <Video className="w-4 h-4 text-white" />}
              </button>
              <button
                onClick={handleCancel}
                className="p-1.5 rounded-full bg-red-600 hover:bg-red-700 transition-colors"
              >
                <Phone className="w-4 h-4 text-white rotate-[135deg]" />
              </button>
            </div>

            {/* Resize handle */}
            <div
              className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize"
              onMouseDown={handleResizeStart}
            >
              <div className="absolute bottom-1 right-1 w-2 h-2 border-r-2 border-b-2 border-white/50" />
            </div>
          </div>
        </div>

        {/* Left: Catalog */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Top row: Search + Session info */}
          <div className="bg-gray-900 p-3 flex-shrink-0">
            {/* Search + Connection status */}
            <div className="flex flex-col gap-3">
              {/* Search bar */}
              <div className="relative w-full">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher un produit par nom ou code..."
                  className="w-full bg-gray-800 text-white rounded-xl pl-12 pr-4 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-green-500 placeholder-gray-500"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
              {/* Connection status */}
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className={`w-2.5 h-2.5 rounded-full ${sessionConnected ? 'bg-green-500' : 'bg-yellow-500'}`} />
                  <span className="text-gray-400">Session: {sessionConnected ? 'Connecté' : 'Connexion...'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-2.5 h-2.5 rounded-full ${rtcConnected ? 'bg-green-500' : 'bg-yellow-500'}`} />
                  <span className="text-gray-400">Vidéo: {rtcConnected ? 'Active' : 'En attente...'}</span>
                </div>
                <button
                  onClick={() => setShowScaleModal(true)}
                  className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-700 transition-colors"
                  title="Ouvrir la balance (tares, détails)"
                >
                  <Scale className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-400">
                    Balance: {scaleConnected ? `${(scaleWeight / 1000).toFixed(3)} kg` : 'Non connectée'}
                  </span>
                  {scaleConnected && <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />}
                </button>
              </div>
            </div>
          </div>

          {/* Breadcrumb */}
          <div className="bg-white border-b px-4 py-2 flex items-center gap-2 flex-shrink-0">
            {breadcrumb.map((item, index) => (
              <div key={index} className="flex items-center">
                {index > 0 && <ChevronLeft className="w-4 h-4 text-gray-400 rotate-180 mx-1" />}
                <button
                  onClick={() => navigateToCategory(item.id, item.name)}
                  className={`text-sm ${index === breadcrumb.length - 1 ? 'font-medium text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  {item.name}
                </button>
              </div>
            ))}
          </div>

          {/* Categories + Products Grid */}
          <div className="flex-1 overflow-auto p-4">
            {catalogLoading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
              </div>
            ) : (
              <>
                {/* Subcategories */}
                {subCategories.length > 0 && !searchQuery && (
                  <div className="mb-4">
                    <div className="grid grid-cols-4 gap-3">
                      {subCategories.map((cat) => (
                        <button
                          key={cat.id}
                          onClick={() => navigateToCategory(cat.id, cat.name)}
                          className="bg-white border-2 border-gray-200 rounded-xl p-4 text-center hover:border-green-500 hover:bg-green-50 transition-all"
                        >
                          <span className="text-2xl mb-1 block">{cat.icon || '📁'}</span>
                          <span className="font-medium text-gray-900 text-sm">{cat.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Products */}
                {displayedProducts.length > 0 ? (
                  <div className="grid grid-cols-4 gap-3">
                    {displayedProducts.map((product) => {
                      const price = product.prices?.[0]?.amount_cents || 0;
                      return (
                        <button
                          key={product.id}
                          onClick={() => handleAddProduct(product)}
                          className="bg-white border rounded-xl p-3 text-left hover:shadow-md hover:border-green-500 transition-all group"
                        >
                          {product.image_url ? (
                            <img
                              src={product.image_url}
                              alt={product.name}
                              className="w-full h-20 object-cover rounded-lg mb-2"
                            />
                          ) : (
                            <div className="w-full h-20 bg-gray-100 rounded-lg mb-2 flex items-center justify-center text-3xl">
                              🛒
                            </div>
                          )}
                          <div className="flex items-start justify-between gap-1">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 text-sm truncate">{product.name}</p>
                              <div className="flex items-center gap-1 mt-0.5">
                                {product.bio && <span className="text-xs bg-green-100 text-green-700 px-1 rounded">BIO</span>}
                                {product.is_weighted && <span className="text-xs bg-blue-100 text-blue-700 px-1 rounded">Poids</span>}
                              </div>
                            </div>
                            <span className="text-green-600 font-bold text-sm whitespace-nowrap">
                              {product.is_weighted ? `${(price / 100).toFixed(2)} €/kg` : `${(price / 100).toFixed(2)} €`}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center text-gray-500 py-12">
                    {searchQuery ? 'Aucun produit trouvé' : 'Sélectionnez une catégorie'}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Right: Cart + Chat */}
        <div className="w-80 bg-white border-l flex flex-col flex-shrink-0">
          {/* Cart */}
          <div className="flex-1 overflow-auto p-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <ShoppingCart className="w-5 h-5" />
              Panier client
              <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full">
                {vendorCartState.length}
              </span>
            </h3>

            {vendorCartState.length === 0 ? (
              <div className="text-center text-gray-400 py-8">
                <ShoppingCart className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Panier vide</p>
                <p className="text-xs mt-1">En attente de synchronisation...</p>
              </div>
            ) : (
              <ul className="space-y-2">
                {vendorCartState.map((item, index) => (
                  <li key={`${item.product.id}-${index}`} className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-900 text-sm truncate">{item.product.name}</p>
                          {/* Badge préparé/non préparé */}
                          {item.prepared ? (
                            <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">✓ Prêt</span>
                          ) : (
                            <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">⏳ À préparer</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">
                          {item.product.is_weighted
                            ? `${(item.quantity / 1000).toFixed(3)} kg`
                            : `${item.quantity} x`}
                          <span className="ml-2 text-gray-400">
                            {item.addedBy === 'vendor' ? '(vous)' : '(client)'}
                          </span>
                        </p>
                      </div>
                      <div className="text-right flex items-center gap-2">
                        <p className="font-medium text-gray-900 text-sm">
                          {(item.total_price_cents / 100).toFixed(2)} €
                        </p>
                        <button
                          onClick={() => sendCartAction({ type: 'remove', productId: item.product.id })}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {/* Total */}
            {vendorCartState.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center justify-between text-lg font-bold">
                  <span>Total</span>
                  <span className="text-green-600">{(cartTotal / 100).toFixed(2)} €</span>
                </div>
              </div>
            )}
          </div>

          {/* Customer Suggestions */}
          {suggestions.filter(s => s.status === 'pending').length > 0 && (
            <div className="border-t p-4 bg-yellow-50">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-3">
                <Bell className="w-5 h-5 text-yellow-600" />
                Suggestions client
                <span className="bg-yellow-200 text-yellow-800 text-xs px-2 py-0.5 rounded-full">
                  {suggestions.filter(s => s.status === 'pending').length}
                </span>
              </h3>
              <ul className="space-y-2 max-h-40 overflow-auto">
                {suggestions
                  .filter(s => s.status === 'pending')
                  .map((suggestion) => (
                    <li key={suggestion.id} className="bg-white rounded-lg p-3 border border-yellow-200">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 text-sm truncate">{suggestion.product_name}</p>
                          <p className="text-xs text-gray-500">
                            {suggestion.unit === 'g'
                              ? `${(suggestion.quantity / 1000).toFixed(3)} kg`
                              : `${suggestion.quantity} ${suggestion.unit || 'x'}`}
                            {' - '}
                            {(suggestion.price_cents / 100).toFixed(2)} €
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            // La suggestion vient du client, donc pas besoin d'envoyer cart_action
                            // Le produit sera déjà dans le panier du client (il l'a lui-même suggéré)
                            // On marque juste la suggestion comme approuvée
                            respondToSuggestion(suggestion.id, 'approve');
                          }}
                          className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-medium"
                        >
                          <Check className="w-3.5 h-3.5" />
                          Ajouter
                        </button>
                        <button
                          onClick={() => respondToSuggestion(suggestion.id, 'later')}
                          className="flex items-center justify-center gap-1 px-2 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded text-xs"
                          title="Plus tard"
                        >
                          <Clock className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => respondToSuggestion(suggestion.id, 'reject')}
                          className="flex items-center justify-center gap-1 px-2 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 rounded text-xs"
                          title="Refuser"
                        >
                          <XCircle className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </li>
                  ))}
              </ul>
            </div>
          )}

          {/* Chat */}
          <div className="border-t flex-shrink-0">
            <div className="h-40 overflow-auto p-3 space-y-2">
              {messages.length === 0 ? (
                <div className="text-center text-gray-400 text-sm py-4">
                  Aucun message
                </div>
              ) : (
                messages.map((msg, i) => (
                  <div
                    key={i}
                    className={`flex ${msg.from === 'vendor' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-lg px-3 py-1.5 ${
                        msg.from === 'vendor'
                          ? 'bg-green-100 text-green-900'
                          : 'bg-gray-100 text-gray-900'
                      }`}
                    >
                      <p className="text-sm">{msg.message}</p>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="p-3 border-t flex gap-2">
              <input
                type="text"
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder="Message..."
                className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <button
                onClick={handleSendMessage}
                disabled={!chatMessage.trim()}
                className="p-2 bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Weight dialog for weighted products */}
      {showWeightDialog && selectedWeightedProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-96 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {selectedWeightedProduct.name}
              </h3>
              <button onClick={handleWeightCancel} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-500 mb-2">Prix: {((selectedWeightedProduct.prices?.[0]?.amount_cents || 0) / 100).toFixed(2)} €/kg</p>

              {/* Scale status */}
              <div className="flex items-center gap-2 mb-3">
                <Scale className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-500">
                  {scaleConnected ? (
                    <>Balance: <span className="font-medium text-gray-900">{(scaleWeight / 1000).toFixed(3)} kg</span> {scaleStable && <span className="text-green-600">(stable)</span>}</>
                  ) : (
                    <>Balance non connectée <button onClick={() => connectScale()} className="text-blue-500 hover:underline ml-1">Connecter</button></>
                  )}
                </span>
              </div>

              {/* Weight input */}
              <label className="block text-sm font-medium text-gray-700 mb-1">Poids (grammes)</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  value={manualWeight}
                  onChange={(e) => setManualWeight(e.target.value)}
                  placeholder="Ex: 250"
                  className="flex-1 border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  autoFocus
                />
                {scaleConnected && scaleWeight > 0 && (
                  <button
                    onClick={() => setManualWeight(String(scaleWeight))}
                    className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm hover:bg-blue-200"
                  >
                    Utiliser balance
                  </button>
                )}
              </div>

              {/* Price preview */}
              {manualWeight && parseFloat(manualWeight) > 0 && (
                <div className="mt-3 p-3 bg-green-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">{(parseFloat(manualWeight) / 1000).toFixed(3)} kg</span>
                    <span className="text-xl font-bold text-green-600">
                      {((parseFloat(manualWeight) / 1000) * ((selectedWeightedProduct.prices?.[0]?.amount_cents || 0) / 100)).toFixed(2)} €
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleWeightCancel}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleWeightConfirm}
                disabled={!manualWeight || parseFloat(manualWeight) <= 0}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Ajouter au panier
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scale Modal */}
      <ScaleModal
        isOpen={showScaleModal}
        onClose={() => setShowScaleModal(false)}
      />
    </div>
  );
}
